import { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";

export const getNewAccessioningFields = (
    sectionId: string,
    state: {
        studyOptions?: any[];
        periodOptions?: any[];
        timepointOptions?: any[];
        selectedStudyCode?: string | null;
        selectedPeriod?: string | null;
        dosingStationOptions?: any[];
    }
): FormFieldConfig[] => {
    const { studyOptions = [], periodOptions = [], timepointOptions = [], selectedStudyCode, selectedPeriod, dosingStationOptions = [] } = state;
    const isPeriodSelected = Boolean(selectedPeriod);

    switch (sectionId) {
        case "protocol":
            return [
                {
                    name: "studycode",
                    label: "Study Code",
                    type: "select",
                    placeholder: "Select Study Code",
                    required: true,
                    options: studyOptions,
                    disabled: true,
                    rules: {
                        required: "Study Protocol is required",
                    }
                },
            ];
        case "period":
            return [
                {
                    name: "period",
                    label: "Clinical Period",
                    type: "select",
                    required: true,
                    disabled: !selectedStudyCode,
                    options: periodOptions,
                    rules: {
                        required: "Clinical Period is required",
                    }
                },
                {
                    name: "timepoint",
                    label: "Time Point",
                    type: "select",
                    required: true,
                    disabled: !selectedStudyCode || !isPeriodSelected,
                    options: timepointOptions,
                    rules: {
                        required: "Time Point is required",
                    }
                },
                {
                    name: "dosingStation",
                    label: "Dosing Station",
                    type: "select",
                    required: true,
                    disabled: !selectedStudyCode || !isPeriodSelected,
                    options: dosingStationOptions,
                    rules: {
                        required: "Dosing Station is required",
                    }
                },
            ];
        default:
            return [];
    }
};

export const getScanFields = (
    step: "subjectToVacutainer" | "vacutainerToAliquot",
    state: {
        isSessionCompleted?: boolean;
        step1Status?: "idle" | "matched" | "mismatch";
        step2Status?: "idle" | "matched" | "mismatch";
        isSpeaking?: boolean;
        isPending?: boolean;
        totalAliquots?: number;
        activeAliquotIndex?: number;
    }
): FormFieldConfig[] => {
    const { isSessionCompleted, step1Status, step2Status, isSpeaking, isPending, totalAliquots = 1, activeAliquotIndex = 1 } = state;

    if (step === "subjectToVacutainer") {
        return [
            {
                name: "subjectBarcode",
                label: "1. Scan Subject Barcode",
                type: "text",
                placeholder: isSessionCompleted ? "Session Completed" : "Enter subject barcode...",
                disabled: step1Status === "matched" || isSpeaking || isPending || isSessionCompleted
            },
            {
                name: "vacutainerBarcode",
                label: "2. Scan Vacutainer Barcode",
                type: "text",
                placeholder: isSessionCompleted ? "Session Completed" : (step1Status !== "matched" ? "Locked - Scan subject first" : "Enter vacutainer barcode..."),
                disabled: step1Status !== "matched" || step2Status === "matched" || isSpeaking || isPending || isSessionCompleted
            }
        ];
    } else {
        const fields: FormFieldConfig[] = [
            {
                name: "vacutainerBarcode",
                label: "1. Scan Vacutainer Barcode",
                type: "text",
                placeholder: isSessionCompleted ? "Session Completed" : "Enter vacutainer barcode...",
                disabled: step1Status !== "idle" || isSpeaking || isPending || isSessionCompleted
            }
        ];

        for (let i = 1; i <= totalAliquots; i++) {
            const isActive = step1Status !== "idle" && i === activeAliquotIndex;
            fields.push({
                name: `aliquotBarcode_${i}`,
                label: `${i + 1}. Scan Aliquot ${i} Barcode`,
                type: "text",
                placeholder: isSessionCompleted ? "Session Completed" : (!isActive ? "Locked" : `Enter aliquot ${i} barcode...`),
                disabled: !isActive || step2Status === "matched" || isSpeaking || isPending || isSessionCompleted
            });
        }

        return fields;
    }
};

export const accessioningSessionColumns: Column[] = [
    { key: "sessionid", label: "Session ID" },
    { key: "studycode", label: "Study Code" },
    { key: "period_timepoint", label: "Period & Timepoint", textCenter: true },
    { key: "subjects", label: "Subjects" },
    { key: "vacutainers", label: "Vacutainers" },
    { key: "aliquots", label: "Aliquots" },
    { key: "status", label: "Status", textCenter: true },
    { key: "lastactive", label: "Last Active" },
    { key: "action", label: "Action" },
];

export const scanLogColumns: Column[] = [
    { key: "scandate", label: "Date" },
    { key: "scantime", label: "Time" },
    { key: "barcode1", label: "Source Barcode" }, // Dynamically change label in UI
    { key: "barcode2", label: "Destination Barcode" }, // Dynamically change label in UI
    { key: "status", label: "Status" },
    { key: "result", label: "Result" },
];

export const accessioningStatsCards = (stats: any) => [
    {
        title: "TOTAL SUBJECTS",
        value: stats?.subjects || 0,
        icon: "ri-group-line",
        bgColor: "#ecfdf5",
        iconColor: "#10b981",
        trendType: "info",
        subtextIcon: "ri-arrow-left-down-line"
    },
    {
        title: "VACUTAINERS SCANNED",
        value: stats?.vacutainers || 0,
        icon: "ri-test-tube-line",
        bgColor: "#fef3c7",
        iconColor: "#d97706",
        trendType: "negative",
        subtextIcon: "ri-error-warning-line"
    },
    {
        title: "ALIQUOTS SEPARATED",
        value: stats?.aliquots || 0,
        icon: "ri-flask-line",
        bgColor: "#ecfdf5",
        iconColor: "#10b981",
        trendType: "success",
        subtextIcon: "ri-arrow-up-line"
    },
    {
        title: "PENDING SEPARATIONS",
        value: stats?.pending || 0,
        icon: "ri-time-line",
        bgColor: "#fef3c7",
        iconColor: "#d97706",
        trendType: "negative",
        subtextIcon: "ri-error-warning-line"
    }
];

export const REPORT_TYPE_OPTIONS = [
    { label: "Summary Report", value: "SUMMARY" },
    { label: "History Report", value: "HISTORY" },
];

export const getGenerateReportFormFields = (
    periodOptions: any[] = [],
    subjectOptions: any[] = [],
    isSampleSeparation: boolean = false,
    isFetchingSubjects: boolean = false
): FormFieldConfig[] => {

    const fields: FormFieldConfig[] = [
        {
            name: "period",
            label: "Period No",
            type: "select",
            options: periodOptions,
            placeholder: "Select Period",
            isClearable: false,
        },
        {
            name: "subjectno",
            label: "Subject No",
            type: "select",
            required: true,
            options: subjectOptions,
            placeholder: isFetchingSubjects ? "Loading subjects..." : "Select Subject",
            isClearable: false,
        },
    ];

    if (!isSampleSeparation) {
        fields.push({
            name: "reporttype",
            label: "Report Type",
            type: "select",
            options: REPORT_TYPE_OPTIONS,
            placeholder: "Select Report Type",
            isClearable: false,
        });
    }

    return fields;
};
